import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Reveal, { SectionHead } from "./Reveal";
import { submitWebinar } from "@/lib/api";

const OUTLINE = [
  "Why digital invisibility harms ministry reach",
  "The three platforms churches should prioritize",
  "The difference between posting and digital discipleship",
  "The four elements every church needs online",
  "The F.O.L.W.A.C. proof-of-concept story",
  "Invitation to the Founding Church Partner Cohort",
];

export default function Webinar() {
  const [form, setForm] = useState({ name: "", email: "", church_name: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitWebinar(form);
      setDone(true);
      toast.success("You’re registered. Watch your inbox for details.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="webinar" data-testid="webinar-section" className="py-20 lg:py-32 px-4 sm:px-6 lg:px-8 bg-navy-surface/40">
      <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <SectionHead eyebrow="Free Live Webinar"
            title="The Church Is Still Here: How Small Churches Can Build a Powerful Digital Ministry Presence in 30 Days" />
          <Reveal delay={0.1}>
            <ul className="mt-10 space-y-4" data-testid="webinar-outline">
              {OUTLINE.map((o) => (
                <li key={o} className="flex items-start gap-3 text-sm text-slate-200">
                  <Check size={16} className="text-gold mt-0.5 shrink-0" />{o}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="rounded-lg border border-gold/25 bg-navy/80 backdrop-blur-xl p-8 shadow-[0_8px_32px_rgba(0,0,0,0.37)]">
            {done ? (
              <div className="py-10 text-center" data-testid="webinar-form-success">
                <span className="mx-auto flex items-center justify-center w-14 h-14 rounded-full bg-gold text-navy mb-5">
                  <Check size={26} />
                </span>
                <p className="font-display text-xl uppercase text-white">You’re on the list.</p>
                <p className="mt-3 text-sm text-slate-300">Reserve your seat — details and the schedule will follow by email.</p>
              </div>
            ) : (
              <form onSubmit={onSubmit} className="space-y-4" data-testid="webinar-form">
                <p className="font-display text-lg uppercase tracking-tight text-white">Reserve Your Seat</p>
                <input data-testid="webinar-name-input" className="field" placeholder="Your name" required
                  value={form.name} onChange={set("name")} />
                <input data-testid="webinar-email-input" className="field" type="email" placeholder="Email address" required
                  value={form.email} onChange={set("email")} />
                <input data-testid="webinar-church-input" className="field" placeholder="Church name (optional)"
                  value={form.church_name} onChange={set("church_name")} />
                <input data-testid="webinar-role-input" className="field" placeholder="Your role (Pastor, Administrator…)"
                  value={form.role} onChange={set("role")} />
                <button data-testid="webinar-form-submit-button" disabled={busy}
                  className="w-full rounded-full bg-gold px-6 py-4 text-[11px] font-bold tracking-[0.18em] uppercase text-navy transition-all hover:bg-gold-hover hover:shadow-[0_0_25px_rgba(212,175,55,0.35)] disabled:opacity-60 flex items-center justify-center gap-2">
                  {busy && <Loader2 size={16} className="animate-spin" />}
                  Register for the Webinar
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
}
